import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Sticky banner shown app-wide whenever a super admin is impersonating
 * another tenant. Reads from localStorage on mount + listens for the
 * 'storage' event so other tabs stay in sync. Provides the only way out
 * of impersonation (clears the swapped tokens + reloads).
 */
const IMPERSONATION_KEYS = [
  'impersonating_account_name',
  'auth_token_original',
  'auth_user_original',
] as const;

const clearImpersonationKeys = () => {
  IMPERSONATION_KEYS.forEach((k) => localStorage.removeItem(k));
};

/**
 * Returns the active impersonation name only if all the supporting state
 * is present (active auth_token + saved actor token). If anything's stale
 * (logged out, token cleared, etc.), wipes the orphan keys and returns null.
 */
const readActiveImpersonation = (): string | null => {
  const name = localStorage.getItem('impersonating_account_name');
  const activeToken = localStorage.getItem('auth_token');
  const originalToken = localStorage.getItem('auth_token_original');
  if (name && activeToken && originalToken) return name;
  if (name || originalToken || localStorage.getItem('auth_user_original')) {
    clearImpersonationKeys();
  }
  return null;
};

const ImpersonationBanner: React.FC = () => {
  const [name, setName] = useState<string | null>(readActiveImpersonation);

  useEffect(() => {
    const sync = () => setName(readActiveImpersonation());
    window.addEventListener('storage', sync);
    // Re-check periodically — covers the case where AuthContext cleared
    // auth_token in the same tab without firing a 'storage' event.
    const id = window.setInterval(sync, 1000);
    return () => {
      window.removeEventListener('storage', sync);
      window.clearInterval(id);
    };
  }, []);

  if (!name) return null;

  const exit = () => {
    const originalToken = localStorage.getItem('auth_token_original');
    const originalUser = localStorage.getItem('auth_user_original');
    if (!originalToken) {
      toast.error('No original session to restore — log in again');
      return;
    }
    localStorage.setItem('auth_token', originalToken);
    if (originalUser) localStorage.setItem('auth_user', originalUser);
    localStorage.removeItem('auth_token_original');
    localStorage.removeItem('auth_user_original');
    localStorage.removeItem('impersonating_account_name');
    toast.success('Exited impersonation. Reloading…');
    setTimeout(() => window.location.reload(), 300);
  };

  return (
    <div className="sticky top-0 z-50 bg-amber-100 dark:bg-amber-900/40 border-b border-amber-400 px-4 py-2 flex items-center justify-between text-sm shadow-sm">
      <span className="text-amber-900 dark:text-amber-100">
        <strong>Viewing as:</strong> {name}
      </span>
      <button
        onClick={exit}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-200 hover:bg-amber-300 dark:bg-amber-800 dark:hover:bg-amber-700 dark:text-white rounded"
      >
        <XMarkIcon className="h-3 w-3" />
        Exit impersonation
      </button>
    </div>
  );
};

export default ImpersonationBanner;
